import type { ConfirmView } from "@vaulted/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { errorMessage, get, post } from "../api";
import { time12 } from "../components/AppointmentDialog";
import { Alert, Button, Loading } from "../components/ui";
import { Frame } from "./Forms";

const dayLabel = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });

/**
 * "Tap to confirm" from a booking text: the visit (first names and times only), one Confirm button
 * (a tap, not just opening the link: phones open links on their own for previews), then the forms.
 */
export function ConfirmPage() {
  const { token = "" } = useParams();
  const base = `/api/forms/${encodeURIComponent(token)}`;
  const view = useQuery({ queryKey: ["confirm", token], queryFn: () => get<ConfirmView>(`${base}/visit`), retry: false });
  const confirm = useMutation({ mutationFn: () => post<ConfirmView>(`${base}/confirm`, {}), onSuccess: (v) => v.formsNeeded && window.location.assign(`/forms/${token}`) });
  const v = confirm.data ?? view.data;
  if (view.isLoading) return <Loading />;
  if (!v) return <Frame clinic="" title="Your visit"><Alert>{errorMessage(view.error)}</Alert></Frame>;
  return (
    <Frame clinic={v.clinicName} title="Your visit">
      <div className="flex flex-col gap-4">
        <h1 className="font-display text-2xl">{v.confirmed ? "You're confirmed" : "Confirm your visit"}</h1>
        <ul className="flex flex-col gap-2">
          {v.visits.map((x, i) => (
            <li key={i} className="rounded-[var(--radius-control)] border border-line bg-white px-4 py-3">
              <span className="block font-semibold">
                {dayLabel(x.day)} at {time12(`${x.day}T${x.time}`)}
              </span>
              <span className="text-sm text-ink-soft">
                {x.firstName}: {x.service}
              </span>
            </li>
          ))}
        </ul>
        {confirm.error && <Alert>{errorMessage(confirm.error)}</Alert>}
        {v.over ? (
          <p className="text-ink-soft">This visit has passed.</p>
        ) : !v.confirmed ? (
          <Button variant="primary" busy={confirm.isPending} onClick={() => confirm.mutate()}>
            {v.formsNeeded ? "Confirm and fill out forms" : "Confirm"}
          </Button>
        ) : v.formsNeeded ? (
          <Button variant="primary" onClick={() => window.location.assign(`/forms/${token}`)}>
            Fill out forms
          </Button>
        ) : (
          <p className="text-ink-soft">See you then. Need to change it? Call or text us.</p>
        )}
      </div>
    </Frame>
  );
}
